import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Send } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Switch } from "../ui/switch";
import { ImageUpload } from "./ImageUpload";
import map from "../../data/cgDistricts.json";

const SERVICES = [["banking", "Banking Services (AEPS/DMT)"], ["payments", "Digital Payments"], ["bbps", "Utility Payment Centre"], ["insurance", "Insurance"], ["travel", "Travel"], ["essential", "Essential Services"], ["credit", "Credit Services"]];

const Card = ({ title, children }) => (
  <div className="bento space-y-5"><h3 className="font-display text-lg font-semibold">{title}</h3>{children}</div>
);

const Input = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
    <input className="mt-2 h-10 w-full rounded-lg border border-white/10 bg-ink px-3 text-sm outline-none focus:border-saffron" data-testid={testid} {...p} />
  </label>
);

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-start justify-between gap-4">
    <div><p className="text-sm font-medium">{label}</p>{hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}</div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export const SettingsPanel = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!s) return <p className="text-slate-400">Loading…</p>;
  const set = (k) => (v) => setS({ ...s, [k]: v?.target ? v.target.value : v });
  const setStat = (i, k, v) => setS({ ...s, stats: s.stats.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
  const toggleSvc = (k, on) => setS({ ...s, services_enabled: on ? [...s.services_enabled, k] : s.services_enabled.filter((x) => x !== k) });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS({ ...data, whatsapp_ready: s.whatsapp_ready });
      toast.success("Saved — live on the website now");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/followups/send-digest");
      const msg = { sent: `Sent — ${data?.count} leads listed`, nothing_pending: "No pending follow-ups — nothing to send", no_email: "Add an alert email address first", failed: "Email failed to send" };
      toast.message(msg[data?.result] ?? String(data?.result));
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  const sendWeekly = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/weekly-report/send");
      const msg = { sent: "Weekly report sent", no_email: "Add an alert email address first", failed: "Email failed to send" };
      toast.message(msg[data?.result] ?? String(data?.result));
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  const testAlert = async (lang) => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/alerts/test", null, { params: { lang } });
      toast.message(`Email: ${data?.email ?? "-"} · WhatsApp: ${data?.whatsapp ?? "-"}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  return (
    <div className="space-y-5 pb-24" data-testid="admin-settings">
      <div className="grid gap-5 xl:grid-cols-2">
        <Card title="Automation">
          <Toggle label="Accept new applications" hint="Turn off to pause the join form on the website." checked={s.accepting_leads} onChange={set("accepting_leads")} testid="setting-accepting-leads" />
          <Toggle label="Auto-approve new leads" hint="New applications are marked “approved” automatically instead of “new”." checked={s.auto_approve} onChange={set("auto_approve")} testid="setting-auto-approve" />
          <Toggle label="Require mobile OTP" hint="Applicants must verify their mobile with an OTP (currently demo mode — OTP shown on screen)." checked={s.otp_required} onChange={set("otp_required")} testid="setting-otp-required" />
          <Toggle label="Status-change SMS to applicant" hint="Demo mode — the SMS text is saved in the lead's history, not actually sent yet." checked={s.status_sms_on} onChange={set("status_sms_on")} testid="setting-status-sms" />
        </Card>
        <Card title="New lead alerts">
          <Toggle label="Email alert" hint="Get an email for every new application." checked={s.alert_email_on} onChange={set("alert_email_on")} testid="setting-alert-email-on" />
          <Input label="Alert email address" type="email" placeholder="you@yourdomain.in" value={s.alert_email} onChange={set("alert_email")} testid="setting-alert-email" />
          <Toggle label="WhatsApp alert" hint={s.whatsapp_ready ? "Twilio WhatsApp connected." : "Twilio keys not added yet — WhatsApp alerts will not send."} checked={s.alert_whatsapp_on} onChange={set("alert_whatsapp_on")} testid="setting-alert-whatsapp-on" />
          <Input label="Alert WhatsApp number" placeholder="98XXXXXXXX" value={s.alert_whatsapp_to} onChange={set("alert_whatsapp_to")} testid="setting-alert-whatsapp" />
          <p className="text-xs text-slate-400">Alert emails are sent in Hindi when the applicant filled the form in Hindi.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => testAlert("en")} disabled={testing} className="btn-ghost" data-testid="settings-test-alert-btn">
              {testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Test alert (English)
            </button>
            <button onClick={() => testAlert("hi")} disabled={testing} className="btn-ghost" data-testid="settings-test-alert-hi-btn">
              <Send size={16} /> Test alert (Hindi)
            </button>
          </div>
          <p className="text-xs text-slate-400">Save changes before sending a test.</p>
          <div className="border-t border-white/10 pt-5">
            <Toggle label="Daily follow-up email (9:00 AM IST)" hint="Emails the alert address a list of leads still marked “new” after 24 hours. Skipped when nothing is pending." checked={s.digest_on} onChange={set("digest_on")} testid="setting-digest-on" />
            <button onClick={sendDigest} disabled={testing} className="btn-ghost mt-4" data-testid="settings-send-digest-btn"><Send size={16} /> Send follow-up email now</button>
          </div>
          <div className="border-t border-white/10 pt-5">
            <Toggle label="Weekly growth report (Monday 9:00 AM IST)" hint="New, onboarded and rejected partners by state for the last 7 days." checked={s.weekly_on} onChange={set("weekly_on")} testid="setting-weekly-on" />
            <button onClick={sendWeekly} disabled={testing} className="btn-ghost mt-4" data-testid="settings-send-weekly-btn"><Send size={16} /> Send weekly report now</button>
          </div>
        </Card>
        <Card title="Announcement bar">
          <Toggle label="Show announcement" checked={s.announcement_on} onChange={set("announcement_on")} testid="setting-announcement-on" />
          <Input label="Text (English)" value={s.announcement_en} onChange={set("announcement_en")} testid="setting-announcement-en" />
          <Input label="Text (Hindi)" value={s.announcement_hi} onChange={set("announcement_hi")} testid="setting-announcement-hi" />
        </Card>
        <Card title="Contact details">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Phone" value={s.phone} onChange={set("phone")} testid="setting-phone" />
            <Input label="Email" value={s.email} onChange={set("email")} testid="setting-email" />
            <Input label="WhatsApp number" value={s.whatsapp} onChange={set("whatsapp")} testid="setting-whatsapp" />
            <Input label="Address" value={s.address} onChange={set("address")} testid="setting-address" />
            <Input label="CEO / Founder name" value={s.ceo_name} onChange={set("ceo_name")} testid="setting-ceo-name" />
            <Input label="CEO title (English)" value={s.ceo_title_en} onChange={set("ceo_title_en")} testid="setting-ceo-title-en" />
            <Input label="CEO title (Hindi)" value={s.ceo_title_hi} onChange={set("ceo_title_hi")} testid="setting-ceo-title-hi" />
            <div className="sm:col-span-2"><ImageUpload value={s.ceo_photo} onChange={set("ceo_photo")} testid="setting-ceo-photo" label="CEO photo (optional — initials shown if empty)" /></div>
          </div>
        </Card>
        <Card title="Services shown on website">
          {SERVICES.map(([k, l]) => (
            <Toggle key={k} label={l} checked={s.services_enabled.includes(k)} onChange={(on) => toggleSvc(k, on)} testid={`setting-service-${k}`} />
          ))}
        </Card>
      </div>
      <Card title="Chhattisgarh coverage map">
        <p className="text-xs text-slate-400">Tick the districts where you have partner shops. The map section stays hidden on the website until at least one is ticked.</p>
        <div className="flex flex-wrap gap-2" data-testid="setting-districts">
          {map.districts.map(({ name }) => {
            const on = (s.covered_districts ?? []).includes(name);
            return (
              <button key={name} type="button" onClick={() => setS({ ...s, covered_districts: on ? s.covered_districts.filter((x) => x !== name) : [...(s.covered_districts ?? []), name] })}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-300 ${on ? "border-saffron bg-saffron text-ink" : "border-white/15 text-slate-300 hover:border-white/40"}`}
                data-testid={`setting-district-${name.replace(/\s+/g, "-").toLowerCase()}`}>
                {name}
              </button>
            );
          })}
        </div>
      </Card>
      <Card title="Impact numbers">
        <div className="grid gap-4 md:grid-cols-2">
          {s.stats.map((st, i) => (
            <div key={i} className="grid grid-cols-[90px_1fr_1fr] gap-3 rounded-xl border border-white/5 p-3">
              <Input label="Value" value={st.value} onChange={(e) => setStat(i, "value", e.target.value)} testid={`setting-stat-value-${i}`} />
              <Input label="Label EN" value={st.label_en} onChange={(e) => setStat(i, "label_en", e.target.value)} testid={`setting-stat-en-${i}`} />
              <Input label="Label HI" value={st.label_hi} onChange={(e) => setStat(i, "label_hi", e.target.value)} testid={`setting-stat-hi-${i}`} />
            </div>
          ))}
        </div>
      </Card>
      <div className="fixed bottom-6 right-6 z-40">
        <button onClick={save} disabled={busy} className="btn-primary btn-lg shadow-2xl" data-testid="settings-save-btn">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save changes
        </button>
      </div>
    </div>
  );
};
